<script setup>
import DeleteAudienceAction from "@frontJs/components/actions/projectManagement/sma/DeleteAudienceAction.vue";
import { router } from "@inertiajs/vue3";
import ActionButton from "@js/components/ActionButton.vue";
import InputField from "@js/components/fields/InputField.vue";
import ListboxField from "@js/components/fields/ListboxField.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import { goBack, useRoute } from "@js/utils/route";
import { Form } from "vee-validate";
import { computed, reactive, ref } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import { string, object } from "yup";

const { t } = useI18n();

const { tenant, facebookAdsAudience, facebookAdsAudienceTypes } = defineProps({
  tenant: {
    required: true,
    type: Object,
  },
  facebookAdsAudience: {
    required: false,
    type: Object,
  },
  facebookAdsAudienceTypes: {
    required: true,
    type: Array,
  },
});

const { route } = useRoute();

const form = reactive({
  type: facebookAdsAudience
    ? facebookAdsAudienceTypes.find(
        (type) => type.value === facebookAdsAudience.type.value,
      )
    : facebookAdsAudienceTypes[0],
  data: facebookAdsAudience ? facebookAdsAudience.data : {},
  name: facebookAdsAudience ? facebookAdsAudience.name : "",
});

const isSubmitting = ref(false);

const selectedTab = ref(0);

const validationSchema = computed(() => {
  let schema = {
    type: object().required(t("error.required")),
    name: string().required(t("error.required")),
  };

  if (
    form.type.value === "customers_who_have_bought_n_time" ||
    form.type.value === "customers_who_have_never_bought_last_n_days" ||
    form.type.value === "customers_who_have_bought_more_than_n_time" ||
    form.type.value === "customers_who_have_unprocessed_cart_last_n_days" ||
    form.type.value === "customers_who_have_bought_last_n_days" ||
    form.type.value ===
      "customers_who_have_not_bought_last_n_days_but_bought_before" ||
    form.type.value ===
      "customers_who_have_not_bought_last_n_days_but_bought_last_t_days"
  ) {
    schema = {
      ...schema,
      data: object().shape({
        n: string().required(t("error.required")),
      }),
    };
  }

  if (
    form.type.value ===
      "customers_who_have_bought_more_than_n_time_last_t_days" ||
    form.type.value ===
      "customers_who_have_not_bought_last_n_days_but_bought_last_t_days"
  ) {
    schema = {
      ...schema,
      data: object().shape({
        n: string().required(t("error.required")),
        t: string().required(t("error.required")),
      }),
    };
  }

  return object(schema);
});

function changeTab(index) {
  selectedTab.value = index;
}

function saveAction() {
  isSubmitting.value = true;

  fetch(
    route("front.projectManagement.sma.postAudience", {
      tenant: tenant.id,
      ...(facebookAdsAudience
        ? { facebookAdsAudienceId: facebookAdsAudience.id }
        : {}),
    }),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
        Accept: "application/json",
      },
      body: JSON.stringify({
        type: form.type.value,
        data: form.data,
        name: form.name,
      }),
    },
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return response.json();
    })
    .then(() => {
      toast.success(t("toast.successfullySaved"), {
        position: "top-right",
      });
      router.visit(
        route(
          "front.projectManagement.sma.audiences",
          tenant ? { tenant: tenant.id } : {},
        ),
      );
    })
    .finally(() => {
      isSubmitting.value = false;
    })
    .catch((error) => {
      toast.error(error.message, { position: "top-right" });
      router.visit(
        route("front.projectManagement.sma.audiences", {
          tenant: tenant.id,
        }),
      );
    });
}

function deleteCallback() {
  toast.success(t("toast.successfullyDeleted"));
  router.visit(
    route("front.projectManagement.sma.audiences", { tenant: tenant.id }),
  );
}
</script>

<template>
  <Form
    v-slot="{ errors }"
    :validation-schema="validationSchema"
    @submit="saveAction"
  >
    <TabGroup
      :selected-tab="selectedTab"
      :change-tab="changeTab"
      :tabs="{
        information: t('title.information'),
      }"
    >
      <template #panels>
        <TabPanel>
          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2">
            <InputField
              v-model="form.name"
              :label="t('label.name')"
              field-name="name"
              field-id="name"
              type="text"
              required
            />
          </div>

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2">
            <ListboxField
              v-model="form.type"
              field-id="type"
              field-name="type"
              :label="t('label.type')"
              :options="facebookAdsAudienceTypes"
              required
              :disabled="facebookAdsAudience"
            >
              <template #buttonContent>
                {{ form.type.label }}
              </template>

              <template #optionContent="{ option }">
                {{ option.label }}
              </template>
            </ListboxField>
          </div>

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <InputField
              v-if="
                form.type.value === 'customers_who_have_bought_n_time' ||
                form.type.value ===
                  'customers_who_have_unprocessed_cart_last_n_days' ||
                form.type.value ===
                  'customers_who_have_never_bought_last_n_days' ||
                form.type.value ===
                  'customers_who_have_bought_more_than_n_time' ||
                form.type.value ===
                  'customers_who_have_bought_more_than_n_time_last_t_days' ||
                form.type.value === 'customers_who_have_bought_last_n_days' ||
                form.type.value ===
                  'customers_who_have_not_bought_last_n_days_but_bought_before' ||
                form.type.value ===
                  'customers_who_have_not_bought_last_n_days_but_bought_last_t_days'
              "
              v-model="form.data.n"
              :label="t('label.n')"
              field-name="data.n"
              field-id="data.n"
              type="number"
              step="1"
              min="1"
              required
            />

            <InputField
              v-if="
                form.type.value ===
                  'customers_who_have_bought_more_than_n_time_last_t_days' ||
                form.type.value ===
                  'customers_who_have_not_bought_last_n_days_but_bought_last_t_days'
              "
              v-model="form.data.t"
              :label="t('label.t')"
              field-name="data.t"
              field-id="data.t"
              type="number"
              step="1"
              min="1"
              required
            />
          </div>
        </TabPanel>
      </template>
    </TabGroup>

    <div
      class="flex flex-wrap items-center gap-2 rounded-b border border-gray-200 bg-white p-6"
    >
      <ActionButton type="transparent" :callback="() => goBack()">
        {{ t("button.back") }}
      </ActionButton>

      <DeleteAudienceAction
        v-if="facebookAdsAudience"
        :facebook-ads-audience="facebookAdsAudience"
        :tenant="tenant"
        :callback="deleteCallback"
      >
        <template #button="{ setIsOpen }">
          <ActionButton type="secondary" :callback="() => setIsOpen(true)">
            {{ t("button.delete") }}
          </ActionButton>
        </template>
      </DeleteAudienceAction>

      <ActionButton
        submit
        :disabled="Object.keys(errors).length > 0 || isSubmitting"
        :loading="isSubmitting"
      >
        <template v-if="!isSubmitting">
          {{ t("button.save") }}
        </template>

        <template v-else>
          {{ t("button.saveInProgress") }}
        </template>
      </ActionButton>

      <div v-if="Object.keys(errors).length > 0" class="text-sm text-red-500">
        {{ t("error.formHasErrors") }}
        {{ Object.values(errors).join(", ") }}
      </div>
    </div>
  </Form>
</template>
